import { collection, addDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebase";

// Sample order data for testing
const sampleOrders = [
  {
    clientEmail: "john.doe@example.com",
    clientName: "John Doe",
    productType: "Family Tree",
    status: "pending",
    totalAmount: 150.0,
    createdAt: Timestamp.fromDate(
      new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    ), // 7 days ago
    adminNotes: "",
    statusHistory: [
      {
        status: "pending",
        timestamp: Timestamp.fromDate(
          new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        ),
        reason: "order-created",
        automated: true,
      },
    ],
  },
  {
    clientEmail: "jane.smith@example.com",
    clientName: "Jane Smith",
    productType: "Missionary Art",
    status: "in-progress",
    totalAmount: 200.0,
    createdAt: Timestamp.fromDate(
      new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)
    ), // 5 days ago
    adminNotes: "Working on design revisions",
    statusHistory: [
      {
        status: "pending",
        timestamp: Timestamp.fromDate(
          new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)
        ),
        reason: "order-created",
        automated: true,
      },
      {
        status: "in-progress",
        timestamp: Timestamp.fromDate(
          new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
        ),
        reason: "admin-update",
        notes: "Starting work on design",
        adminUserId: "admin",
      },
    ],
  },
  {
    clientEmail: "bob.wilson@example.com",
    clientName: "Bob Wilson",
    productType: "Generational Art",
    status: "completed",
    totalAmount: 175.0,
    createdAt: Timestamp.fromDate(
      new Date(Date.now() - 14 * 24 * 60 * 60 * 1000)
    ), // 14 days ago
    adminNotes: "Customer very satisfied with final result",
    statusHistory: [
      {
        status: "pending",
        timestamp: Timestamp.fromDate(
          new Date(Date.now() - 14 * 24 * 60 * 60 * 1000)
        ),
        reason: "order-created",
        automated: true,
      },
      {
        status: "in-progress",
        timestamp: Timestamp.fromDate(
          new Date(Date.now() - 10 * 24 * 60 * 60 * 1000)
        ),
        reason: "admin-update",
        adminUserId: "admin",
      },
      {
        status: "completed",
        timestamp: Timestamp.fromDate(
          new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
        ),
        reason: "admin-update",
        notes: "Order completed and delivered",
        adminUserId: "admin",
      },
    ],
  },
  {
    clientEmail: "alice.brown@example.com",
    clientName: "Alice Brown",
    productType: "Photo Family Tree",
    status: "awaiting-info",
    totalAmount: 125.0,
    createdAt: Timestamp.fromDate(
      new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
    ), // 3 days ago
    adminNotes: "Waiting for additional family photos",
    statusHistory: [
      {
        status: "pending",
        timestamp: Timestamp.fromDate(
          new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
        ),
        reason: "order-created",
        automated: true,
      },
      {
        status: "awaiting-info",
        timestamp: Timestamp.fromDate(
          new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
        ),
        reason: "admin-update",
        notes: "Need more family photos for complete tree",
        adminUserId: "admin",
      },
    ],
  },
  {
    clientEmail: "charlie.davis@example.com",
    clientName: "Charlie Davis",
    productType: "Family Tree",
    status: "pending",
    totalAmount: 180.0,
    createdAt: Timestamp.fromDate(
      new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
    ), // 1 day ago
    adminNotes: "",
    statusHistory: [
      {
        status: "pending",
        timestamp: Timestamp.fromDate(
          new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
        ),
        reason: "order-created",
        automated: true,
      },
    ],
  },
  // Add some orders from the same clients to test client aggregation
  {
    clientEmail: "john.doe@example.com",
    clientName: "John Doe",
    productType: "Missionary Art",
    status: "completed",
    totalAmount: 220.0,
    createdAt: Timestamp.fromDate(
      new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    ), // 30 days ago
    adminNotes: "Second order from repeat customer",
    statusHistory: [
      {
        status: "pending",
        timestamp: Timestamp.fromDate(
          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        ),
        reason: "order-created",
        automated: true,
      },
      {
        status: "completed",
        timestamp: Timestamp.fromDate(
          new Date(Date.now() - 25 * 24 * 60 * 60 * 1000)
        ),
        reason: "admin-update",
        notes: "Completed successfully",
        adminUserId: "admin",
      },
    ],
  },
];

/**
 * Create sample orders for testing the admin dashboard
 */
export async function createSampleOrders(): Promise<void> {
  try {
    console.log("Creating sample orders...");

    const ordersRef = collection(db, "orders");

    for (const orderData of sampleOrders) {
      const docRef = await addDoc(ordersRef, orderData);
      console.log(`Created order: ${docRef.id} for ${orderData.clientName}`);
    }

    console.log(`✅ Successfully created ${sampleOrders.length} sample orders`);
  } catch (error) {
    console.error("Error creating sample orders:", error);
    throw error;
  }
}

/**
 * Call this function from the browser console to create sample data
 * Example: DevAdminUtils.createSampleData()
 */
export function setupSampleDataFunction(): void {
  if (typeof window !== "undefined") {
    (window as any).DevAdminUtils = {
      ...(window as any).DevAdminUtils,
      createSampleData: createSampleOrders,
    };
  }
}

// Auto-setup when this module is imported
setupSampleDataFunction();
